import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, TextField,
    FormControlLabel, Checkbox, Box, Typography
} from '@mui/material';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const MatterForm = ({ openForm, setOpenForm, onAddLegalMatter, onUpdateLegalMatter, initialData }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    
    // 🎯 FIX: Always initialize as an empty array to prevent .map errors
    const [administrators, setAdministrators] = useState([]);
    // Everyone eligible for the Boedel List (admins + users) - a broader pool than
    // `administrators` above, which is filtered to admins only for the handover picker.
    const [boedelUsers, setBoedelUsers] = useState([]);

    const [formData, setFormData] = useState({
        matterName: '',
        client: '',
        estateNo: '',
        description: '',
        status: 'Pending',
        estateClassification: '',
        isHandedOverToAdministrator: false,
        assignedAdminId: '',
        isOnBoedelList: false,
        boedelUserId: '',
        isSetAside: false,
        setAsideDate: '',
        setAsideReason: '',
        isNotProceeding: false,
        notProceedingDate: '',
        notProceedingReason: '',
        attorneyFirm: '',
        attorneyName: '',
        attorneyPhone: '',
        attorneyEmail: '',
        attorneyRef: '',
        applicantName: '',
        applicantPhone: '',
        applicantEmail: '',
        applicantAddress: '',
        applicantIsDebtor: false,
        isVoluntary: false,
        hasProvisionalOrders: true,
        respondents: [],
    });

    const isEditMode = !!initialData?.id;

    useEffect(() => {
        const fetchData = async () => {
          try {
            const [userRes, adminRes, boedelRes] = await Promise.all([
                axios.get('/api/user', { withCredentials: true }),
                axios.get('/api/users/administrators?adminOnly=true', { withCredentials: true }),
                axios.get('/api/users/administrators', { withCredentials: true })
            ]);

            setUser(userRes.data);

            // 🎯 FIX: Validate that adminRes.data is actually an array before setting state
            if (Array.isArray(adminRes.data)) {
                setAdministrators(adminRes.data);
            } else {
                console.error("Administrators API did not return an array:", adminRes.data);
                setAdministrators([]);
            }

            setBoedelUsers(Array.isArray(boedelRes.data) ? boedelRes.data : []);
          } catch (err) {
            console.error('Error fetching data:', err);
            setAdministrators([]); // Fallback to empty array on error
            setBoedelUsers([]);
          }
        };
      
        if (openForm) fetchData();
    }, [openForm]);

    useEffect(() => {
        if (openForm && initialData) {
            setFormData({
                ...initialData,
                matterName: initialData.matterName || '',
                client: initialData.client || '',
                estateNo: initialData.estateNo || '',
                description: initialData.description || '',
                status: initialData.status || 'Pending',
                estateClassification: initialData.estateClassification || '',
                isHandedOverToAdministrator: initialData.isHandedOverToAdministrator || false,
                // Ensure assignedAdminId maps correctly from the backend relation
                assignedAdminId: initialData.assignedTo?.id || initialData.assignedAdminId || '',
                isOnBoedelList: !!(initialData.boedelUser?.id || initialData.boedelUserId),
                boedelUserId: initialData.boedelUser?.id || initialData.boedelUserId || '',
                isSetAside: initialData.isSetAside || false,
                setAsideDate: initialData.setAsideDate ? String(initialData.setAsideDate).split('T')[0] : '',
                setAsideReason: initialData.setAsideReason || '',
                isNotProceeding: initialData.isNotProceeding || false,
                notProceedingDate: initialData.notProceedingDate ? String(initialData.notProceedingDate).split('T')[0] : '',
                notProceedingReason: initialData.notProceedingReason || '',
                attorneyFirm: initialData.attorneyFirm || '',
                attorneyName: initialData.attorneyName || '',
                attorneyPhone: initialData.attorneyPhone || '',
                attorneyEmail: initialData.attorneyEmail || '',
                attorneyRef: initialData.attorneyRef || '',
                applicantName: initialData.applicantName || '',
                applicantPhone: initialData.applicantPhone || '',
                applicantEmail: initialData.applicantEmail || '',
                applicantAddress: initialData.applicantAddress || '',
                applicantIsDebtor: initialData.applicantIsDebtor || false,
                isVoluntary: initialData.isVoluntary || false,
                hasProvisionalOrders: initialData.hasProvisionalOrders !== false,
                respondents: initialData.respondents || [],
            });
        } else if (openForm && !initialData) {
            setFormData({
                matterName: '', client: '', estateNo: '', description: '', status: 'Pending', estateClassification: '',
                isHandedOverToAdministrator: false, assignedAdminId: '',
                isOnBoedelList: false, boedelUserId: '', isSetAside: false,
                setAsideDate: '', setAsideReason: '', isNotProceeding: false, notProceedingDate: '', notProceedingReason: '',
                attorneyFirm: '', attorneyName: '', attorneyPhone: '', attorneyEmail: '', attorneyRef: '',
                applicantName: '', applicantPhone: '', applicantEmail: '', applicantAddress: '', applicantIsDebtor: false, isVoluntary: false, hasProvisionalOrders: true,
                respondents: []
            });
        }
    }, [openForm, initialData]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const handleSubmit = async () => {
        const trimmedFormData = {
            ...formData,
            matterName: formData.matterName?.trim() || "",
            client: formData.client?.trim() || "",
            // estateNo has a UNIQUE constraint in the DB - send null (not "") when blank so
            // multiple matters without a Master Reference yet don't collide on empty string.
            estateNo: formData.estateNo?.trim() || null,
        };

        if (!trimmedFormData.matterName) {
            alert("Please fill in the Estate Name!");
            return;
        }

        if (formData.isHandedOverToAdministrator && !formData.assignedAdminId) {
            alert("Please select an administrator to hand this matter over to!");
            return;
        }

        if (formData.isOnBoedelList && !formData.boedelUserId) {
            alert("Please select who this matter's Boedel List entry belongs to!");
            return;
        }

        try {
            const url = isEditMode 
                ? `/api/legal-matter/${initialData.id}` 
                : `/api/legal-matter`;
            
            const response = await axios({
                method: isEditMode ? 'PUT' : 'POST',
                url: url,
                data: trimmedFormData,
                withCredentials: true,
                headers: { 'x-user-id': user?.id } 
            });

            if (response.data) {
                if (isEditMode) {
                    onUpdateLegalMatter(response.data.updatedMatter);
                } else {
                    const newMatter = response.data.matter;
                    onAddLegalMatter(newMatter);
                    const route = newMatter.estateClassification === 'Deceased Estates' 
                        ? `/deceased-estate/${newMatter.id}` 
                        : `/matter/${newMatter.id}`;
                    navigate(route);
                }
                setOpenForm(false);
            }
        } catch (error) {
            console.error("Error saving matter:", error.response ? error.response.data : error.message);
            alert("There was an error saving. Please try again.");
        }
    };

    const isInsolvencyOrLiquidation = ['Company', 'CC/BK', 'Person'].includes(formData.estateClassification);
    const isVoluntary = formData.isVoluntary === true || formData.isVoluntary === '1' || formData.isVoluntary === 1 || String(formData.isVoluntary) === 'true';

    return (
        <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="sm">
            <DialogTitle>{isEditMode ? "Edit Matter Details" : "Add Legal Matter"}</DialogTitle>
            <DialogContent>
                <TextField label="Estate Name" name="matterName" fullWidth value={formData.matterName || ''} onChange={handleChange} sx={{ mb: 2, mt: 1 }} />
                <TextField label="Administrators Name" name="client" fullWidth value={formData.client || ''} onChange={handleChange} sx={{ mb: 2 }} />
                <TextField label="Master Reference" name="estateNo" fullWidth value={formData.estateNo || ''} onChange={handleChange} sx={{ mb: 2 }} />

                {!isEditMode && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="estate-classification-label">Estate Classification</InputLabel>
                        <Select labelId="estate-classification-label" name="estateClassification" value={formData.estateClassification || ''} onChange={handleChange} label="Estate Classification">
                            {/* Driven by the user's User Type (set by an admin in User Admin), not their role */}
                            {user?.userType === 'npa' ? [
                                <MenuItem key="admin" value="Administration of Estates">Administration of Estates</MenuItem>,
                                <MenuItem key="deceased" value="Deceased Estates">Deceased Estates</MenuItem>,
                                <MenuItem key="npa" value="NPA">Curator Appointments</MenuItem>,
                                <MenuItem key="divorce" value="Divorce Settlements">Divorce Settlements</MenuItem>
                            ] : [
                                <MenuItem key="company" value="Company">Company</MenuItem>,
                                <MenuItem key="cc" value="CC/BK">CC/BK</MenuItem>,
                                <MenuItem key="person" value="Person">Person</MenuItem>,
                                <MenuItem key="deceased" value="Deceased Estates">Deceased Estates</MenuItem>
                            ]}
                        </Select>
                    </FormControl>
                )}

                {isInsolvencyOrLiquidation && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="matter-type-label">Matter Type</InputLabel>
                        <Select
                            labelId="matter-type-label"
                            name="isVoluntary"
                            value={formData.isVoluntary === true ? 'true' : 'false'}
                            label="Matter Type"
                            onChange={(e) => setFormData(prev => ({ ...prev, isVoluntary: e.target.value === 'true' }))}
                        >
                            <MenuItem value="false">Involuntary (Normal)</MenuItem>
                            <MenuItem value="true">Voluntary (Friendly)</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {isInsolvencyOrLiquidation && formData.isVoluntary && (
                    <FormControlLabel
                        control={<Checkbox name="applicantIsDebtor" checked={formData.applicantIsDebtor} onChange={handleChange} />}
                        label="The Applicant is the Debtor/Insolvent (No separate applicant)"
                        sx={{ mb: 1, mt: 1 }}
                    />
                )}


                {!isVoluntary && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
                            Applicant Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            <TextField label="Applicant Name" name="applicantName" fullWidth value={formData.applicantName || ''} onChange={handleChange} sx={{ gridColumn: 'span 2' }} />
                            <TextField label="Contact Number" name="applicantPhone" fullWidth value={formData.applicantPhone || ''} onChange={handleChange} />
                            <TextField label="Email Address" name="applicantEmail" fullWidth value={formData.applicantEmail || ''} onChange={handleChange} />
                            <TextField label="Physical & Postal Address" name="applicantAddress" fullWidth multiline rows={2} value={formData.applicantAddress || ''} onChange={handleChange} sx={{ gridColumn: 'span 2' }} />
                        </Box>
                    </>
                )}

                {!isVoluntary && formData.estateClassification !== 'NPA' && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
                            {isInsolvencyOrLiquidation && formData.isVoluntary ? 'Attorney Handling Voluntary Application' : 'Applicant Attorney Information'}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            <TextField label="Firm Name" name="attorneyFirm" fullWidth value={formData.attorneyFirm || ''} onChange={handleChange} />
                            <TextField label="Attorney Name" name="attorneyName" fullWidth value={formData.attorneyName || ''} onChange={handleChange} />
                            <TextField label="Contact Number" name="attorneyPhone" fullWidth value={formData.attorneyPhone || ''} onChange={handleChange} />
                            <TextField label="Email Address" name="attorneyEmail" fullWidth value={formData.attorneyEmail || ''} onChange={handleChange} />
                            <TextField label="Reference" name="attorneyRef" fullWidth value={formData.attorneyRef || ''} onChange={handleChange} sx={{ gridColumn: 'span 2' }} />
                        </Box>
                    </>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <FormControlLabel control={<Checkbox name="hasProvisionalOrders" checked={formData.hasProvisionalOrders} onChange={handleChange} />} label="Matter includes Provisional Orders" />
                    <FormControlLabel control={<Checkbox name="isHandedOverToAdministrator" checked={formData.isHandedOverToAdministrator} onChange={handleChange} />} label="Handed over to Administrator" />
                    
                    {formData.isHandedOverToAdministrator && (
                        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                            <InputLabel id="admin-select-label">Select Administrator</InputLabel>
                            <Select
                                labelId="admin-select-label"
                                name="assignedAdminId" 
                                value={formData.assignedAdminId}
                                label="Select Administrator"
                                onChange={handleChange}
                            >
                                {/* 🎯 FIX: Added Optional Chaining and Array check for safety */}
                                {Array.isArray(administrators) && administrators.map((admin) => (
                                    <MenuItem key={admin.id} value={admin.id}>
                                        {admin.firstName} {admin.lastName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Every matter appears on the Boedel List automatically - this only adds it to
                        one user's personal "My List" filter there, it's not required for the
                        matter to show up at all. */}
                    <FormControlLabel control={<Checkbox name="isOnBoedelList" checked={formData.isOnBoedelList} onChange={handleChange} />} label="Add to a User's Personal Boedel List" />

                    {formData.isOnBoedelList && (
                        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                            <InputLabel id="boedel-user-select-label">Assign Personal List To</InputLabel>
                            <Select
                                labelId="boedel-user-select-label"
                                name="boedelUserId"
                                value={formData.boedelUserId}
                                label="Assign Personal List To"
                                onChange={handleChange}
                            >
                                {Array.isArray(boedelUsers) && boedelUsers.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.firstName} {u.lastName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControlLabel control={<Checkbox name="isSetAside" checked={formData.isSetAside} onChange={handleChange} color="error" />} label="Matter Set Aside (Stop Reminders)" />
                    {formData.isSetAside && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pl: 4, mb: 1 }}>
                            <TextField label="Date Set Aside" name="setAsideDate" type="date" InputLabelProps={{ shrink: true }} fullWidth value={formData.setAsideDate || ''} onChange={handleChange} inputProps={{ max: '9999-12-31' }} />
                            <TextField label="Reason for Set Aside" name="setAsideReason" fullWidth multiline rows={2} value={formData.setAsideReason || ''} onChange={handleChange} sx={{ gridColumn: 'span 2' }} />
                        </Box>
                    )}

                    <FormControlLabel control={<Checkbox name="isNotProceeding" checked={formData.isNotProceeding} onChange={handleChange} color="error" />} label="Matter Not Proceeding (for another reason)" />
                    {formData.isNotProceeding && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pl: 4, mb: 1 }}>
                            <TextField label="Date Decided Not to Proceed" name="notProceedingDate" type="date" InputLabelProps={{ shrink: true }} fullWidth value={formData.notProceedingDate || ''} onChange={handleChange} inputProps={{ max: '9999-12-31' }} />
                            <TextField label="Reason for Not Proceeding" name="notProceedingReason" fullWidth multiline rows={2} value={formData.notProceedingReason || ''} onChange={handleChange} sx={{ gridColumn: 'span 2' }} />
                        </Box>
                    )}
                </Box>

                {/* NPA matters: Defendants / Respondents are captured on the matter's Estate
                    Information page instead - this form only allowed one name before it
                    navigated there on submit, so the section was removed from here. */}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => setOpenForm(false)}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {isEditMode ? "Update" : "Submit"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MatterForm;